import React, { useContext, useEffect, useRef } from 'react';
import { css } from '@emotion/react';
import Image from 'gatsby-plugin-sanity-image';
import { ThemeContext } from './theme-provider';
import PortableText from './portable-text';
import Close from '../svg/close.svg';
import useAnimatedStatic from './use-animated-static';

export default function ModalContent({ title, content }) {
  const modal = useRef(null);
  const closeButton = useRef(null);
  const theme = useContext(ThemeContext);
  const backgroundPosition = useAnimatedStatic(theme.showModal);

  useEffect(() => {
    if (theme.showModal) {
      const previousFocus = document.activeElement;
      const previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      closeButton.current.focus();
      const onKeyDown = event => {
        if (event.key === 'Escape') theme.setShowModal(false);
        if (event.key !== 'Tab') return;
        const focusable = [...modal.current.querySelectorAll('button, a[href], input, select, textarea, [tabindex="0"]')];
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      };
      document.addEventListener('keydown', onKeyDown);
      return () => {
        document.removeEventListener('keydown', onKeyDown);
        document.body.style.overflow = previousOverflow;
        if (previousFocus?.isConnected) previousFocus.focus();
      };
    }
  }, [theme.showModal]);

  return (
    <div
      ref={modal}
      role="dialog"
      aria-modal="true"
      aria-label={theme.modalTitle || 'More information'}
      css={css`
        display: ${(theme.showModal) ? 'block' : 'none'};
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        background-image: url('/images/background-static.png');
      `}
      style={{
        backgroundPosition,
      }}
    >
      <button
        ref={closeButton}
        type="button"
        aria-label="Close information"
        css={css`
          position: absolute;
          top: var(--margin);
          right: calc(var(--bigMargin) + var(--gutter));
          cursor: pointer;
          background: none;
          border: 0;
          padding: 0;
          min-width: 44px;
          min-height: 44px;
          &:focus-visible { outline: 2px solid var(--black); outline-offset: 4px; }

          @media (max-width: 1080px) {
            top: 14.5rem;
          }

          @media (max-width: 700px) {
            top: 12rem;
            right: 4rem;
          }
        `}
        onClick={() => {
          theme.setShowModal(false);
        }}
      >
        <Close
          css={css`
            position: relative;
            top: -2.5rem;
            width: 6rem;
            height: auto;
            overflow: visible;
            z-index: 1;

            &:hover {
              circle, path {
                stroke: var(--primary);
                transition: stroke 0s;
              }
            }

            circle, path {
              stroke: var(--black);
              transition: stroke 0.6s var(--curve);
            }
          `}
        />
      </button>
      <div
        className="type--45"
        css={css`
          position: absolute;
          top: 0;
          right: var(--bigMargin);
          bottom: 0;
          left: var(--bigMargin);
          color: var(--black);
          background-color: var(--white);
          padding: var(--margin);
          overflow-y: scroll;

          @media (max-width: 1080px) {
            top: 10rem;
          }

          @media (max-width: 700px) {
            top: 7.5rem;
            right: var(--gutter);
            left: var(--gutter);
          }
        `}
      >
        {theme.modalImage && <Image
          {...theme.modalImage}
          width={990}
          style={{
            display: 'block',
            width: '100%',
            maxWidth: '500px',
            margin: '0 auto',
            marginBottom: '5rem',
          }}
          loading="eager"
        />}
        {theme.modalTitle && <div
          css={css`
            text-transform: uppercase;
            margin-bottom: 5rem;
          `}
        >
          {theme.modalTitle}
        </div>}
        {theme.modalContent && <PortableText
          blocks={theme.modalContent}
        />}
      </div>
    </div>
  )
}
